import { Controller } from "@hotwired/stimulus"

const FILTER_LABELS = {
  ingredients: v => v,
  meal_type: v => v === "uncategorized" ? "Not Categorized" : v,
  sort: v => ({ name: "A-Z", name_desc: "Z-A", newest: "Recent", oldest: "Oldest" }[v] || v),
  cuisine: v => v,
  protein: v => v,
  cooking_method: v => v,
  diet: v => v,
  favorites: () => "Favorites",
  quick: () => "Under 30 min",
  few_ingredients: () => "Few Ingredients",
  period: v => ({ today: "Today", week: "This Week", month: "This Month", year: "This Year" }[v] || v)
}

export default class extends Controller {
  static targets = ["pill", "source", "sourceSelect", "frame", "dropdown", "checkbox", "viewToggle", "viewInput", "chips", "filterPanel", "filterToggle", "moreFilters"]
  static classes = ["active", "inactive"]
  static values = { url: String }

  connect() {
    this.debounceTimer = null
    this.boundClose = this.closeDropdowns.bind(this)
    document.addEventListener("click", this.boundClose)
  }

  disconnect() {
    clearTimeout(this.debounceTimer)
    document.removeEventListener("click", this.boundClose)
  }

  changeSource() {
    const value = this.sourceSelectTarget.value
    this.clearCheckboxes()

    if (value.startsWith("cookbook_")) {
      this.sourceTarget.value = "cookbooks"
      const cookbookId = value.replace("cookbook_", "")
      this.checkboxTargets.forEach(cb => {
        if (cb.name === "cookbook_ids[]") cb.checked = cb.value === cookbookId
      })
    } else {
      this.sourceTarget.value = value
    }

    this.refresh()
  }

  selectSource(event) {
    const pill = event.currentTarget
    const key = pill.dataset.source
    const hasDropdown = pill.dataset.dropdown

    this.pillTargets.forEach(p => {
      const isActive = p.dataset.source === key
      this.activeClasses.forEach(cls => p.classList.toggle(cls, isActive))
      this.inactiveClasses.forEach(cls => p.classList.toggle(cls, !isActive))
    })

    this.sourceTarget.value = key

    if (hasDropdown) {
      this.toggleDropdown(key, pill)
    } else {
      this.closeDropdowns()
      this.clearCheckboxes()
      this.refresh()
    }
  }

  toggleDropdown(key, pill) {
    const dropdown = this.dropdownTargets.find(d => d.dataset.source === key)
    if (!dropdown) return

    const isVisible = !dropdown.classList.contains("hidden")
    this.dropdownTargets.forEach(d => d.classList.add("hidden"))

    if (!isVisible) {
      const rect = pill.getBoundingClientRect()
      dropdown.style.top = `${rect.bottom + 4}px`
      dropdown.classList.remove("hidden")
    } else {
      this.refresh()
    }
  }

  closeDropdowns(event) {
    if (event) {
      const isInsideDropdown = this.dropdownTargets.some(d => d.contains(event.target))
      const isPill = this.pillTargets.some(p => p.contains(event.target))
      if (isInsideDropdown || isPill) return
    }
    this.dropdownTargets.forEach(d => d.classList.add("hidden"))
  }

  selectAll() {
    this.clearCheckboxes()
    this.closeDropdowns()
    this.refresh()
  }

  applyFilter() {
    this.closeDropdowns()
    this.refresh()
  }

  setView(event) {
    const view = event.currentTarget.dataset.view
    this.viewInputTarget.value = view
    this.updateViewButtons(view)

    if (view === "swipe") {
      this.openSwipe()
    } else {
      this.refresh()
    }
  }

  updateViewButtons(activeView) {
    this.viewToggleTargets.forEach(toggle => {
      toggle.querySelectorAll("button").forEach(btn => {
        const isActive = btn.dataset.view === activeView
        btn.classList.toggle("bg-warm-600", isActive)
        btn.classList.toggle("text-white", isActive)
        btn.classList.toggle("bg-white", !isActive)
        btn.classList.toggle("dark:bg-gray-800", !isActive)
        btn.classList.toggle("text-gray-400", !isActive)
      })
    })
  }

  async openSwipe() {
    const params = this.buildParams()
    params.set("view", "swipe")
    const response = await fetch(`${this.urlValue}?${params}`)
    const html = await response.text()
    document.body.insertAdjacentHTML("beforeend", html)
  }

  removeFilter(event) {
    const name = event.currentTarget.dataset.filterName
    const form = this.filterForm()
    form.querySelectorAll(`[name="${name}"]`).forEach(el => {
      if (el.type === "checkbox") {
        el.checked = false
      } else {
        el.value = ""
      }
    })
    this.refresh()
  }

  clearAll() {
    const form = this.filterForm()
    form.querySelectorAll("select").forEach(s => { s.selectedIndex = 0 })
    form.querySelectorAll('input[type="checkbox"]').forEach(cb => { cb.checked = false })
    form.querySelectorAll('input[type="search"]').forEach(i => { i.value = "" })
    this.clearCheckboxes()
    this.refresh()
  }

  clearCheckboxes() {
    this.checkboxTargets.forEach(cb => { cb.checked = false })
  }

  // Resolve the filter form. Prefer a form explicitly marked with
  // data-dashboard-filter-target="form"; otherwise fall back to the one
  // targeting the dashboard_recipes turbo frame; last resort, first form.
  filterForm() {
    return this.element.querySelector('form[data-turbo-frame="dashboard_recipes"]') ||
           this.element.querySelector("form")
  }

  buildParams() {
    const form = this.filterForm()
    const params = new URLSearchParams(new FormData(form))

    this.checkboxTargets.forEach(cb => {
      if (cb.checked) params.append(cb.name, cb.value)
    })

    return params
  }

  refresh() {
    const params = this.buildParams()
    this.frameTarget.src = `${this.urlValue}?${params}`
    this.updateChips(params)
  }

  updateChips(params) {
    const container = this.chipsTarget
    container.innerHTML = ""
    let hasChips = false
    const skip = new Set(["source", "view", "cookbook_ids[]"])
    const rendered = new Set()

    for (const [key, value] of params.entries()) {
      if (!value || skip.has(key)) continue
      if (rendered.has(key)) continue
      const labelFn = FILTER_LABELS[key]
      if (!labelFn) continue

      rendered.add(key)
      hasChips = true
      const chip = document.createElement("button")
      chip.type = "button"
      chip.dataset.action = "dashboard-filter#removeFilter"
      chip.dataset.filterName = key
      chip.className = "inline-flex items-center gap-1 rounded-full bg-warm-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-warm-700 transition-colors"
      chip.innerHTML = `${labelFn(value)}<svg class="h-2.5 w-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12" /></svg>`
      container.appendChild(chip)
    }

    if (hasChips) {
      const clear = document.createElement("button")
      clear.type = "button"
      clear.dataset.action = "dashboard-filter#clearAll"
      clear.className = "text-xs text-gray-400 hover:text-gray-600 transition-colors ml-1"
      clear.textContent = "Clear all"
      container.appendChild(clear)
    }

    container.classList.toggle("hidden", !hasChips)
    container.classList.toggle("flex", hasChips)
  }

  toggleFilters() {
    const panel = this.filterPanelTarget
    const btn = this.filterToggleTarget
    const isHidden = panel.classList.contains("hidden")

    panel.classList.toggle("hidden", !isHidden)
    btn.classList.toggle("border-warm-400", isHidden)
    btn.classList.toggle("text-warm-600", isHidden)
    btn.classList.toggle("bg-warm-50", isHidden)
    btn.classList.toggle("border-warm-200", !isHidden)
    btn.classList.toggle("text-gray-500", !isHidden)
    btn.classList.toggle("bg-white", !isHidden)
  }

  search() {
    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.refresh(), 300)
  }

  // Mirror a changed select to any sibling input with the same name.
  // Used for Sort / Category which render in both the desktop inline row
  // and the mobile filter panel.
  syncAndRefresh(event) {
    const input = event.target
    const name = input.name
    const value = input.value
    this.element.querySelectorAll(`[name="${name}"]`).forEach(el => {
      if (el !== input) el.value = value
    })
    this.refresh()
  }

  toggleMoreFilters() {
    if (this.hasMoreFiltersTarget) this.moreFiltersTarget.classList.toggle("hidden")
  }
}
